import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '~/client/components/ui/select'
import { ArrowLeft } from 'lucide-react'
import { useCreateShareableList } from '~/client/shareable-list/use-create-shareable-list.hook'
import { useEffectiveUserDetail } from '~/client/common/stores/auth'
import { categorizeVenueGroups, type VenueGroupWithId } from '~/client/common/cityTiers'
import { toast } from 'sonner'
import { MinimalTiptap } from '~/client/components/ui/shadcn-io/minimal-tiptap'

type Props = {
  spaceId?: number
  onBack?: () => void
  onCancel?: () => void
  onDone: () => void
}

export function CreateShareableListStep({ spaceId, onBack, onCancel, onDone }: Props) {
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [venueGroupId, setVenueGroupId] = React.useState<number | undefined>(undefined)
  const createMutation = useCreateShareableList()
  const navigate = useNavigate()

  const userDetail = useEffectiveUserDetail()
  const venueGroups: VenueGroupWithId[] = userDetail?.active_entity?.venue_groups ?? []
  const { premiumGroups, basicSorted } = React.useMemo(() => categorizeVenueGroups(venueGroups), [venueGroups])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || !venueGroupId) return

    try {
      const result = await createMutation.mutateAsync({ name: trimmed, description: description.trim() || undefined, spaceId, venueGroupId })
      toast.success(
        spaceId
          ? 'List created and space added'
          : 'Shareable list created',
        {
          description: result.list.name,
          position: 'bottom-right',
        },
      )
      onDone()
      // Redirect only for standalone creation; the spaceId flow keeps the user in their listings/event context.
      if (!spaceId && result.list?.slug) {
        void navigate({ to: '/collection/$slug', params: { slug: result.list.slug }, search: { tab: 'search' } })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create collection'
      toast.error(message, { position: 'bottom-right', richColors: true })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="shareable-list-name" className="text-[14px] font-medium text-[#55534e]">
          Collection Name
        </Label>
        <Input
          id="shareable-list-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Stunning PDRs for 25 near Midtown"
          autoFocus
          className="h-11 rounded-[8px]"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[14px] font-medium text-[#55534e]">
          City <span className="text-red-500">*</span>
        </Label>
        <Select
          value={venueGroupId !== undefined ? String(venueGroupId) : undefined}
          onValueChange={(val) => setVenueGroupId(Number(val))}
        >
          <SelectTrigger className="h-11 rounded-[8px]">
            <SelectValue placeholder="Select a city" />
          </SelectTrigger>
          <SelectContent>
            {premiumGroups.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-[#8a3dff] font-semibold">Premium Data</SelectLabel>
                {premiumGroups.map((vg) => (
                  <SelectItem key={vg.id} value={String(vg.id)}>
                    {vg.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {basicSorted.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-[#95919a] font-semibold">Essential Data</SelectLabel>
                {basicSorted.map((vg) => (
                  <SelectItem key={vg.id} value={String(vg.id)}>
                    {vg.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[14px] font-medium text-[#55534e]">
          Description
        </Label>
        <MinimalTiptap
          initialContent={description}
          onChange={setDescription}
          placeholder="Our team's go-to venues for small-scale networking events"
          visibleTools={['bold', 'italic', 'bulletList', 'orderedList']}
          showToolbarOnFocusOnly
          onChangeDelayMs={0}
          className="rounded-[8px] border border-input bg-white"
        />
      </div>

      <div className="flex items-center justify-between pt-2">
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-[#95919a] hover:text-[#55534e]"
          >
            <ArrowLeft className="size-4 mr-1" />
            Back
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="text-[#95919a] hover:text-[#55534e]"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="revel"
          className="h-10 rounded-[10px] px-6"
          disabled={!name.trim() || !venueGroupId || createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </form>
  )
}
